import React, {PropsWithChildren, useState} from 'react';
import {Profile} from '../../types/api';
import {icons} from './icons';
import {
    Box,
    Container,
    Divider,
    Drawer,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Typography,
    useTheme,
    IconButton,
} from '@mui/material';
import {useTranslation} from 'react-i18next';
import CircleBorder from '../Ui/CircleBorder';
import {config} from '../../config';
import {useA2HS} from '../../hooks/useA2HS';
import appleWallet from '../../assets/images/wallet/add-to-apple-wallet.png';
import googleWallet from '../../assets/images/wallet/add-to-google-wallet.png';
import {
    isAndroid,
    isChrome,
    isDesktop,
    isIOS,
    isSafari,
} from 'react-device-detect';
import {getUtmParams} from '../../lib/tracking';
import {useMatomo} from '@jonkoops/matomo-tracker-react';
import {createStateSetterProxy} from '../../lib/utils';
import {StateSetter} from '../../types/utils';
import {useModals} from '../../hooks/useModalStack';
import ProfileOfflineDialog from './ProfileOfflineDialog';
import useWalletHandler from './hooks/useWalletHandler';
import {
    addToAppleWallet,
    addToGoogleWallet,
} from '../../services/api/ProfileApi';
import {useWindowSize} from '../../hooks/useWindowSize.ts';

type Props = PropsWithChildren<{
    profile: Profile;
    safeKey: string;
    qrOnline: string | undefined;
    qrOffline: string | undefined;
}>;

export default function ProfileMenu({
    profile,
    safeKey,
    qrOnline,
    qrOffline,
}: Props) {
    const {openModal} = useModals();
    const [open, _setOpen] = useState(false);
    const {t} = useTranslation();
    const theme = useTheme();
    const {ready: A2HSReady, prompt: onA2HSClick, accepted} = useA2HS();
    const {innerWidth} = useWindowSize();
    const size = Math.min(innerWidth / 2, 300);
    const buttonBgColor = !open ? `rgba(255,255,255,0.7)` : undefined;
    const {trackEvent} = useMatomo();
    const {createWalletPassHandler, walletLoading} = useWalletHandler({
        profileId: profile.id,
        safeKey,
        offline: false,
    });

    const setOpen = React.useCallback<StateSetter<boolean>>(
        handler => {
            _setOpen(
                createStateSetterProxy(handler, open => {
                    trackEvent({
                        category: 'interaction',
                        action: 'toggle',
                        name: 'menu',
                        value: open ? 1 : 0,
                    });

                    return open;
                }),
            );
        },
        [_setOpen],
    );

    return (
        <>
            <IconButton
                size="large"
                edge="start"
                color="inherit"
                aria-label={t('app:profile_menu.toggle', 'Menu')}
                sx={theme => ({
                    'position': 'absolute',
                    'top': 10,
                    'right': 10,
                    'zIndex': theme.zIndex.drawer + 1,
                    'backgroundColor': buttonBgColor,
                    '&:hover': {
                        backgroundColor: buttonBgColor,
                    },
                })}
                onClick={() => setOpen(p => !p)}
            >
                <icons.menuBars />
            </IconButton>
            <Drawer anchor={'right'} open={open} onClose={() => setOpen(false)}>
                <CircleBorder color={theme.palette.secondary.main}>
                    <Container
                        maxWidth={'xs'}
                        sx={{
                            pt: 3,
                            pb: 1,
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                textAlign: 'center',
                            }}
                        >
                            <div
                                style={{
                                    borderRadius: '50%',
                                    backgroundColor: '#FFF',
                                    padding: 3,
                                    width: size,
                                    height: size,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                <img
                                    style={{
                                        display: 'block',
                                        width: '68%',
                                    }}
                                    src={qrOnline}
                                    alt={'QRCode'}
                                />
                            </div>
                            <Typography
                                variant={'body2'}
                                sx={{
                                    mt: 2,
                                }}
                            >
                                {t(
                                    'app:profile_menu.qrcode.helper',
                                    'Show QRCode to share your profile',
                                )}
                            </Typography>
                            <List
                                sx={{
                                    mt: 3,
                                }}
                            >
                                {(isIOS || (isDesktop && isSafari)) && (
                                    <ListItem>
                                        <ListItemButton
                                            disableRipple={true}
                                            disabled={walletLoading}
                                            onClick={createWalletPassHandler(
                                                addToAppleWallet,
                                            )}
                                        >
                                            <img
                                                style={{maxHeight: 40}}
                                                src={appleWallet}
                                                alt="Apple Wallet"
                                            />
                                        </ListItemButton>
                                    </ListItem>
                                )}
                                {(isAndroid || (isDesktop && isChrome)) && (
                                    <ListItem>
                                        <ListItemButton
                                            disableRipple={true}
                                            disabled={walletLoading}
                                            onClick={createWalletPassHandler(
                                                addToGoogleWallet,
                                            )}
                                        >
                                            <img
                                                style={{maxHeight: 40}}
                                                src={googleWallet}
                                                alt="Google Wallet"
                                            />
                                        </ListItemButton>
                                    </ListItem>
                                )}
                            </List>
                        </Box>
                    </Container>
                </CircleBorder>
                <List
                    sx={{
                        mt: 3,
                    }}
                >
                    <ListItem disablePadding>
                        <ListItemButton
                            component={'a'}
                            href={`${config.homeUrl}?${getUtmParams({
                                medium: 'profile',
                                source: 'h-menu',
                                term: profile.id,
                            })}`}
                        >
                            <ListItemIcon>
                                <icons.buy />
                            </ListItemIcon>
                            <ListItemText
                                primary={t(
                                    'app:profile_menu.action.buy',
                                    'Get my VKARD',
                                )}
                            />
                        </ListItemButton>
                    </ListItem>
                    {A2HSReady && (
                        <ListItem disablePadding>
                            <ListItemButton
                                onClick={!accepted ? onA2HSClick : undefined}
                                color={accepted ? 'success' : 'undefined'}
                            >
                                <ListItemIcon>
                                    <icons.addToHomeScreen />
                                </ListItemIcon>
                                <ListItemText
                                    primary={t(
                                        'app:profile_menu.action.a2hs',
                                        'Add to home screen',
                                    )}
                                />
                            </ListItemButton>
                        </ListItem>
                    )}
                    <Divider />
                    <ListItem disablePadding>
                        <ListItemButton
                            component={'a'}
                            href={
                                profile.organization?.dashboardUrl ??
                                config.dashboardUrl
                            }
                        >
                            <ListItemIcon>
                                <icons.accountTrigger />
                            </ListItemIcon>
                            <ListItemText
                                primary={t(
                                    'app:profile_menu.action.login',
                                    'Dashboard access',
                                )}
                            />
                        </ListItemButton>
                    </ListItem>
                    <ListItem disablePadding>
                        <ListItemButton
                            onClick={() => {
                                openModal(ProfileOfflineDialog, {
                                    profile,
                                    safeKey: safeKey,
                                    qrOffline: qrOffline,
                                });
                            }}
                        >
                            <ListItemIcon>
                                <icons.airplaneOffline />
                            </ListItemIcon>
                            <ListItemText
                                primary={t(
                                    'app:profile_menu.action.offline',
                                    'Offline sharing',
                                )}
                            />
                        </ListItemButton>
                    </ListItem>
                </List>
            </Drawer>
        </>
    );
}
